/**
 * @desc CMP loader helper.
 */
class Cmp {
  constructor() {
    this.registeredVendors = [];
  }

  /**
   *
   * @param {Element} what The created script element
   * @param {Boolean} vendorAllowed User consent regarding the vendor related to the script
   */
  // eslint-disable-next-line class-methods-use-this
  applyWithConsent(what, vendorAllowed, callback) {
    const firstScriptOnPage = document.getElementsByTagName('script')[0];
    if (vendorAllowed) {
      firstScriptOnPage.parentNode.insertBefore(what, firstScriptOnPage);
      if (callback) callback();
    }
  }

  /**
   * Tests for strings in id (which implies a custom vendor). IAB vendor IDs are numeric only.
   * @param {String|Number} id
   * @return {boolean}
   */
  // eslint-disable-next-line class-methods-use-this
  isIABVendor(id) {
    return !Number.isNaN(parseInt(id, 10));
  }

  /**
   *
   * @param {Object} consents
   * @param {String|Number} vendorId
   * @return {boolean}
   */
  // eslint-disable-next-line class-methods-use-this
  getConsentState(consents, vendorId) {
    const vendorList = consents.vendorConsents;

    return (typeof (consents) === 'object') && 'gdprApplies' in consents
      && (!consents.gdprApplies
        || (vendorId in vendorList && vendorList[vendorId]));
  }

  /**
   * Public helper especially used by Stroeer to get consent information for Google Ad Manager
   * (IAB 755). We still use __cmp as API here as __tcfapi delivers different results for certain
   * methods (not here though).
   * @param cmp
   * @param {Number} vendorId Vendor Id.
   * @returns {boolean|*}
   * @see https://stroeerdigitalgroup.atlassian.net/wiki/spaces/SDGPUBLIC/pages/1273004657/Einbau+des+Publisher+eignen+CMPs#EinbaudesPublishereignenCMPs-WeitereDienstleister
   */
  getPublicConsentState(api, vendorId) {
    const status = api('consentStatus');
    if (typeof (status) === 'object' && 'consentExists' in status && status.consentExists) {
      // consent data is present, check if consent for vendor is given
      return this.getConsentState(api('getCMPData'), vendorId); // => true, dann Google hat die Zustimmung
    }
    return false;
  }

  /**
   * Wrapper function to ensures availability of data and functions needed to validate user consent.
   * Waits for CMP object to attach "consent" event listener. Applies ad scripts then.
   * This actually is a necessary workaround to implementing CMP via GTM also in regards to the
   * proposed IAB standard. That one states that __cmp should be available asap in order to attach
   * event handlers (init) properly, too.
   * @param what {Element} The created script element
   * @param vendorId {Number} The vendor's id within the CMP tool.
   * @param disregardConsent {Boolean}
   */
  // eslint-disable-next-line class-methods-use-this
  waitForConsent(what, vendorId, disregardConsent, callback) {
    const interval = setInterval(() => {
      // Wait for __cmp to be available, a function and to have a consentStatus
      const cmp = window.__cmp;
      if (typeof cmp === 'function' && typeof cmp('consentStatus') === 'object') {
        const vendorIsAllowed = disregardConsent || this.getConsentState(cmp('getCMPData'), vendorId);
        if (disregardConsent) {
          // eslint-disable-next-line no-console
          console.info(`[CMP] "${vendorId}" doesn't apply to consent and will render: %c${vendorIsAllowed ? 'YES' : 'NO'}`, 'font-weight: 700');
        } else {
          // eslint-disable-next-line no-console
          console.info(`[CMP] "${vendorId}" applies to consent and consent is: %c${vendorIsAllowed ? 'YES' : 'NO'}`, 'font-weight: 700');
        }

        clearInterval(interval);
        this.applyWithConsent(what, vendorIsAllowed, callback);

        // Attach vendor to event listener check only when it isn't initialized already & always.
        if (!disregardConsent) {
          // NOTE Add event listener for vendors which might not be loaded yet
          if (!this.registeredVendors.length) {
            // add a listener once. Then, iterate over all possible registered vendors
            cmp('addEventListener', ['consent', (e, o) => {
              this.registeredVendors.forEach((id) => {
                const eventVendorIsAllowed = this.getConsentState(cmp('getCMPData'), id);
                // eslint-disable-next-line no-console
                console.info(`[CMP] updated. Will ${id} (${o.getVendor(id).name || ''}) render? %c${eventVendorIsAllowed ? 'YES' : 'NO'}`, 'font-weight:700');
                this.applyWithConsent(what, eventVendorIsAllowed, callback);
              });
            }, false], null);
          }

          this.registeredVendors.push(vendorId);
        }
      }
    }, 200);
  }
}

/* eslint-enable no-undef */
export default Cmp;
